import { getComposeLocalService, getRecipientService, getRouterService } from '@/core/container/ioc'

import { getComposeMessageRoute, type ComposeMessageRouteRaw } from '@/routes/user/compose'

import type Contact from '@/data/models/domain/Contact'
import type Template from '@/data/models/domain/Template'
import type ContactList from '@/data/models/domain/ContactList'
import type FilteredView from '@/data/models/domain/filteredViews/FilteredView'
import type SmsSurveyReply from '@/data/models/domain/services/smsSurveys/SmsSurveyReply'

import { getFullName } from '@/utils/fullName'
import { useTrackSmsTemplateUsage } from '@/composition/domain/templateUsageTracking'

export const useComposeMessageNavigation = () => {
  const routerService = getRouterService()
  const recipientService = getRecipientService()
  const composeLocalService = getComposeLocalService()
  const { trackSmsTemplateUsage } = useTrackSmsTemplateUsage()

  const composeMessage = async (params?: ComposeMessageRouteRaw) => {
    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageToContactsLists = async (lists: ContactList[], params?: ComposeMessageRouteRaw) => {
    const recipients = lists.map((list) => recipientService.makeRecipientOptionFromContactList(list))

    await composeLocalService.replaceRecipients(recipients)

    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageToContacts = async (contacts: Contact[], params?: ComposeMessageRouteRaw) => {
    const recipients = contacts.map((contact) => recipientService.makeRecipientOptionFromContact(contact))

    await composeLocalService.replaceRecipients(recipients)

    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageToPhones = async (phones: string[], params?: ComposeMessageRouteRaw) => {
    const recipients = phones.map((phone) => recipientService.makeRecipientOptionFromPhoneNumber(phone))

    await composeLocalService.replaceRecipients(recipients)

    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageToFilteredViews = async (filteredViews: FilteredView[], params?: ComposeMessageRouteRaw) => {
    const recipients = filteredViews.map((filteredView) =>
      recipientService.makeRecipientOptionFromFilteredView(filteredView),
    )

    await composeLocalService.replaceRecipients(recipients)

    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageToSmsSurveyReplies = async (replies: SmsSurveyReply[], params?: ComposeMessageRouteRaw) => {
    const recipients = replies.map((reply) =>
      reply.contact
        ? recipientService.makeRecipientOptionFromContact({
            id: reply.contact.id,
            fullName: getFullName(reply.contact.firstName, reply.contact.lastName),
            phone: null,
            blocked: false,
          })
        : recipientService.makeRecipientOptionFromPhoneNumber(reply.sender),
    )

    await composeLocalService.replaceRecipients(recipients)

    await routerService.push(getComposeMessageRoute(params))
  }

  const composeMessageByTemplate = async (template: Template) => {
    await composeLocalService.replaceMessage(template.content)

    trackSmsTemplateUsage(template.id)

    await routerService.push(getComposeMessageRoute())
  }

  return {
    composeMessage,
    composeMessageToPhones,
    composeMessageToContacts,
    composeMessageToContactsLists,
    composeMessageToFilteredViews,
    composeMessageToSmsSurveyReplies,
    composeMessageByTemplate,
  }
}
